import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import {
    Navigate,
    useLocation,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { me, useApp } from "~/core/store";
import {
    DiscoveryEditor,
    DiscoveryHistory,
    IssueTracker,
    Meta,
    Snacker,
} from "~/organisms";
import Content from "./Content";
import Header from "./Header";
import LoadingScreen from "./LoadingScreen";
import MainSearch from "./MainSearch";
import Menu from "./Menu";
import SettingsActions from "./SettingsActions";
import Visits from "./Visits";
import { ServiceListenerDescription } from "./interfaces";

let initialized = false;

const Main: FC = () => {
    const {
        api: {
            connect,
            installation,
            companyId,
            connection,
            connecting,
            service,
            waiting,
            feeCode,
            project,
            task,
            time,
            feeCategory,
            client,
            group,
            settings,
            employee,
            currency,
            account,
            company,
            me: meApi,
        },
        share,
        settings: { setup: setupSettings },
    } = useApp();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const params = useParams();
    const searchStr = searchParams.toString();
    // @TODO: check for only allowed params in order to prevent XSS
    const appendParams = searchStr ? `&${searchStr}` : "";

    useEffect(() => {
        if (!connection.peek()) {
            connect(params.installation);
        }
    }, [connect, params.installation, connection]);

    useEffect(() => {
        if (connection.value) {
            setupSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection.value]);

    useEffect(() => {
        if (!connection.value || !service.loaded.value || initialized) {
            return;
        }

        const listeners: ServiceListenerDescription[] = [
            ["User.Get", meApi.listen],
            [["ConnectedAccount.List", "ExternalAccount.List"], account.listen],
            ["Task.Subscribe", task.subscribe],
            ["TaskUser.Subscribe", task.subscribeTaskUser],
            ["TaskAssignment.Subscribe", task.assignment.subscribe],
            ["TrackGroup.Subscribe", time.group.listen],
            ["Track.Subscribe", time.track.listenActive],
            ["UserSettings.Get", settings.user.listen],
            ["ProSettings.Get", settings.pro.listen],
            ["CompanySettings.Get", settings.company.listen],
            ["BokSettings.Get", settings.book.listen],
            ["LevSettings.Get", settings.supplier.listen],
            ["Project.List", project.listen],
            ["Client.List", client.listen],
            ["Group.List", group.listen],
            ["Feecode.List", feeCode.listen],
            ["Feecategory.List", feeCategory.listen],
            ["Employee.List", employee.listen],
            ["Currency.List", currency.listen],
            ["Company.List", company.listen],
        ];

        for (const [access, listen] of listeners) {
            const enabled = (Array.isArray(access) ? access : [access])
                .map((s) => s.split("."))
                .every(([name, method]) => service.enabledPeek(name, method));

            if (enabled) {
                listen();
            }
        }

        share.check();

        initialized = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection.value, service.loaded.value]);

    if (connecting.value || waiting.value) {
        return <LoadingScreen />;
    }

    if (!connection.value && !waiting.value && !connecting.value) {
        const failed = params.installation === "_";

        return (
            <Navigate
                to={`/p/login?installation=${params.installation}&continue=${pathname}${appendParams}${failed ? "&error=fail&prompt=force" : ""}`}
            />
        );
    }

    if (
        params.installation !== installation.value ||
        params.company !== companyId.value
    ) {
        return <Navigate to={me("dashboard")} replace />;
    }

    return (
        <div
            data-main
            className="max-w-screen flex h-screen max-h-screen w-screen flex-col overflow-hidden text-[#454554]"
        >
            <Header />
            <Snacker />
            <Visits />
            <Meta />
            <SettingsActions />
            <MainSearch />
            <IssueTracker />
            <DiscoveryEditor />
            <div className="flex h-full">
                <Menu />
                <Content />
            </div>
            <DiscoveryHistory />
        </div>
    );
};

export default Main;
